import { useState, useEffect } from 'react';
import { useI18n } from '../../../../../i18n';
import {
  fetchPersonaProcesosDocumentos,
  type PersonaProcesosDocumentos,
} from '../../../../shared/services/api';

const TIPO_BADGE: Record<string, { bg: string; text: string }> = {
  PE: { bg: '#EBF5FF', text: '#1F3864' },
  PO: { bg: '#ECFDF5', text: '#16A34A' },
  PS: { bg: '#FFF7ED', text: '#C2410C' },
};

export function PersonaProcesosDocsTab({ personaId }: { personaId: string }) {
  const { t } = useI18n();
  const [data, setData] = useState<PersonaProcesosDocumentos | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchPersonaProcesosDocumentos(personaId)
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [personaId]);

  if (loading)
    return <div style={{ padding: 20, color: '#9CA3AF' }}>{t.people.loadingDirectory}</div>;
  if (!data) return <div style={{ padding: 20, color: '#9CA3AF' }}>Error al cargar datos</div>;

  const thStyle: React.CSSProperties = {
    padding: '8px 12px',
    fontWeight: 600,
    fontSize: 12,
    textAlign: 'left',
    background: '#0E243E',
    color: '#fff',
  };
  const tdStyle: React.CSSProperties = {
    padding: '8px 12px',
    fontSize: 13,
    borderBottom: '1px solid #F3F4F6',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Procesos representados */}
      <div>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1F3864', margin: '0 0 8px' }}>
          {t.people.processesRepresented ?? 'Procesos representados'}
        </h3>
        {data.procesosRepresentados.length === 0 && data.procesosResponsable.length === 0 ? (
          <div style={{ color: '#9CA3AF', fontSize: 13 }}>
            {t.people.noProcesses ?? 'Sin procesos asignados'}
          </div>
        ) : (
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <thead>
              <tr>
                <th style={thStyle}>Codigo</th>
                <th style={thStyle}>Nombre</th>
                <th style={thStyle}>Tipologia</th>
                <th style={thStyle}>Grupo</th>
                <th style={thStyle}>Via</th>
              </tr>
            </thead>
            <tbody>
              {data.procesosRepresentados.map((p) => {
                const alsoResp = data.procesosResponsable.find((r) => r.id === p.id);
                const badge = TIPO_BADGE[p.tipologia] ?? { bg: '#F3F4F6', text: '#6B7280' };
                return (
                  <tr key={p.id}>
                    <td
                      style={{
                        ...tdStyle,
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 11,
                      }}
                    >
                      {p.codigo}
                    </td>
                    <td style={tdStyle}>{p.nombre}</td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          background: badge.bg,
                          color: badge.text,
                        }}
                      >
                        {p.tipologia}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, color: '#6B7280' }}>{p.grupoNombre}</td>
                    <td style={{ ...tdStyle, fontSize: 11, color: '#6B7280' }}>
                      Representante{alsoResp ? ` + Responsable (${alsoResp.viasPuesto})` : ''}
                    </td>
                  </tr>
                );
              })}
              {data.procesosResponsable
                .filter((r) => !data.procesosRepresentados.some((p) => p.id === r.id))
                .map((p) => {
                  const badge = TIPO_BADGE[p.tipologia] ?? { bg: '#F3F4F6', text: '#6B7280' };
                  return (
                    <tr key={p.id}>
                      <td
                        style={{
                          ...tdStyle,
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 11,
                        }}
                      >
                        {p.codigo}
                      </td>
                      <td style={tdStyle}>{p.nombre}</td>
                      <td style={tdStyle}>
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                            background: badge.bg,
                            color: badge.text,
                          }}
                        >
                          {p.tipologia}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, color: '#6B7280' }}>{p.grupoNombre}</td>
                      <td style={{ ...tdStyle, fontSize: 11, color: '#6B7280' }}>
                        Responsable ({p.viasPuesto})
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        )}
      </div>

      {/* Documentos creados */}
      <div>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1F3864', margin: '0 0 8px' }}>
          {t.people.documentsCreated ?? 'Documentos creados'}
        </h3>
        {data.documentosCreados.length === 0 ? (
          <div style={{ color: '#9CA3AF', fontSize: 13 }}>
            {t.people.noDocuments ?? 'Sin documentos'}
          </div>
        ) : (
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <thead>
              <tr>
                <th style={thStyle}>Codigo</th>
                <th style={thStyle}>Titulo</th>
                <th style={thStyle}>Estado</th>
                <th style={thStyle}>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {data.documentosCreados.map((d) => (
                <tr key={d.id}>
                  <td
                    style={{ ...tdStyle, fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}
                  >
                    {d.codigo}
                  </td>
                  <td style={tdStyle}>{d.titulo}</td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 500,
                        background: '#F3F4F6',
                      }}
                    >
                      {d.estado}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, color: '#6B7280', fontSize: 12 }}>
                    {new Date(d.creadoEn).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Documentos aprobados */}
      {data.documentosAprobados.length > 0 && (
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1F3864', margin: '0 0 8px' }}>
            {t.people.documentsApproved ?? 'Documentos aprobados'}
          </h3>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <thead>
              <tr>
                <th style={thStyle}>Codigo</th>
                <th style={thStyle}>Titulo</th>
                <th style={thStyle}>Fecha aprobacion</th>
              </tr>
            </thead>
            <tbody>
              {data.documentosAprobados.map((d) => (
                <tr key={d.id}>
                  <td
                    style={{ ...tdStyle, fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}
                  >
                    {d.codigo}
                  </td>
                  <td style={tdStyle}>{d.titulo}</td>
                  <td style={{ ...tdStyle, color: '#6B7280', fontSize: 12 }}>
                    {d.fechaAprobacion ? new Date(d.fechaAprobacion).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
